import BrowserUrlDialog from '../BrowserUrlDialog';
import React, { useEffect } from 'react';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '../../../../../interfaces/webSocketConnectionInfo';
import {
  useGetWebsocketMsg,
  useHandleAvatarWebsocketMsg,
  useHandleBrowserUrlRequestWebsocketMsg,
  useHandleEventWebsocketMsg,
  useHandleFollowMeWebsocketMsg,
  useHandleJoinedToRegionWebsocketMsg,
  useHandleMuteParticipantWebsocketMsg,
  useHandleTeleportingModalWebsocketMsg,
  useHandleUELogWebsocketMsg,
  useHandleWebsocketMsg,
} from '../hooks';
import useWebSocket from 'react-use-websocket';
import {
  mytaverseWebsocketLogger,
  useMytaverse,
} from '../../../../../providers/MytaverseProvider';
import useAsyncEffect from 'use-async-effect';
import { useAvatarMessage } from '../../../providers/MytaverseEventProvider/hooks';
import { useMytaverseEvent } from '../../../providers';
import { useConference } from '../Dolby';
import { useZoom } from '../Zoom/ZoomProvider';
import { useWindowResolution } from '../Pureweb/hooks';
import { useDeviceChange } from '../hooks/devices';
import ROUTES from '../../../../../constants/routes';
import { useNavigate } from 'react-router-dom';

const DashboardWebsocket = () => {
  const { sendMessageToUnreal, getWebsocketUrl, getWebSocketOptions } =
    useMytaverse();
  const navigate = useNavigate();
  const getWebsocketMsg = useGetWebsocketMsg();
  const { customAvatarUrl, sendJSONMessageToWebSocket } = useMytaverse();
  const { ueWebcamScreenName, ueScreenScreenName, isConferenceInitialized } =
    useConference();

  const {
    setCurrentParticipant,
    currentRoom,
    muted,
    currentParticipant,
    gameSound,
    currentSkin,
    currentEvent,
    currentEventParticipant,
    isHideWebUI,
    isUEModalActionInProgress,
  } = useMytaverseEvent();

  const {
    zoomUE5Token,
    zoomConferenceName,
    zoomUser,
    zmClient,
    isCameraOn,
    isSharingToScreen,
    isSharingScreenToUE,
    isCurrentParticipantSpeaking,
  } = useZoom();

  const { avatarMessage } = useAvatarMessage({
    currentSkin,
    currentEvent,
    customAvatarUrl,
  });

  useEffect(() => {
    if (avatarMessage && avatarMessage.downloadAvatarUrl) {
      return;
    }

    const timeout = setTimeout(() => {
      if (currentEvent?.enablePureGamecast) {
        return;
      }

      navigate(ROUTES.AVATAR_CHOOSING(currentEvent?.eventId));
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [avatarMessage, currentEvent]);

  const {
    readyState: websocketReadyState,
    lastJsonMessage,
    sendJsonMessage,
  } = useWebSocket(
    getWebsocketUrl,
    getWebSocketOptions({
      withReconnection: false,
    }),
  );

  const websocketMsg = getWebsocketMsg(
    lastJsonMessage as LastWebsocketJsonMessageType,
  );

  const { openBrowserUrlDialog, setOpenBrowserUrlDialog } =
    useHandleBrowserUrlRequestWebsocketMsg(websocketMsg);
  useHandleWebsocketMsg(websocketMsg);
  useHandleEventWebsocketMsg(websocketMsg);
  useHandleJoinedToRegionWebsocketMsg(websocketMsg);
  useHandleMuteParticipantWebsocketMsg(websocketMsg);
  useHandleFollowMeWebsocketMsg(websocketMsg);
  useHandleTeleportingModalWebsocketMsg(websocketMsg);
  useHandleAvatarWebsocketMsg(websocketMsg);
  useHandleUELogWebsocketMsg(websocketMsg);

  const onSubmitBrowserUrl = async (url: string) => {
    await sendMessageToUnreal({ action: 'SET_BROWSER_URL', url });
    setOpenBrowserUrlDialog(false);
  };
  const { windowResolution } = useWindowResolution();
  useDeviceChange();

  const onCloseBrowserUrlDialog = () => setOpenBrowserUrlDialog(false);

  useEffect(() => {
    if (isConferenceInitialized) {
      sendMessageToUnreal({
        action: 'CHANGE_SCREEN_RESOLUTION',
        width: 1980,
        height: 1080,
      });
    }
  }, [windowResolution]);

  useEffect(() => {
    return () => {
      setCurrentParticipant((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          roomId: null,
          region: null,
          regions: [],
        };
      });

      sendJSONMessageToWebSocket({
        action: WebsocketAction.LeaveRoom,
      });
    };
  }, [sendJSONMessageToWebSocket]);

  useAsyncEffect(() => {
    if (websocketReadyState === 1) {
      mytaverseWebsocketLogger.info('Start listening websocket messages');
      // Check if we are speaking to UE
      //startCommunicationFromUeTimer();
    }
  }, [websocketReadyState]);

  const sendParticipantState = React.useCallback(() => {
    if (!currentRoom) {
      return;
    }

    const currentZoomUser = zmClient.getCurrentUserInfo();

    const message = {
      action: WebsocketAction.ParticipantState,
      speaking: isCurrentParticipantSpeaking || false,
      muted,
      worldSoundLevel: gameSound === 0 ? 0.001 : gameSound,
      participantId: currentParticipant?.participantId,
      zoomToken: zoomUE5Token,
      zoomConferenceName,
      zoomUser: zoomUser
        ? {
            ...zoomUser,
            displayName:
              zoomUser?.displayName ||
              currentParticipant?.name ||
              currentParticipant?.email,
          }
        : {
            ...currentZoomUser,
            displayName:
              currentZoomUser?.displayName ||
              currentParticipant?.name ||
              currentParticipant?.email,
          },
      zoomOwnerWebcamStream:
        !isHideWebUI.flag && isCameraOn && currentZoomUser?.userId
          ? {
              participantId: currentParticipant?.participantId,
              zoomUserId: String(currentZoomUser?.userId),
            }
          : {
              participantId: '',
              zoomUserId: '',
            },
      avatar: avatarMessage,
      isUEModalActionInProgress,
    };

    sendJSONMessageToWebSocket(message);
  }, [
    currentRoom,
    isCurrentParticipantSpeaking,
    currentParticipant,
    currentEventParticipant,
    muted,
    sendJSONMessageToWebSocket,
    gameSound,
    avatarMessage,
    zoomUE5Token,
    zoomConferenceName,
    zoomUser,
    zmClient,
    isCameraOn,
    isSharingToScreen,
    ueWebcamScreenName,
    ueScreenScreenName,
    isSharingScreenToUE,
    isHideWebUI,
    isUEModalActionInProgress,
  ]);

  useEffect(() => {
    const interval = setInterval(async () => {
      await sendParticipantState();
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [sendParticipantState]);

  return openBrowserUrlDialog ? (
    <BrowserUrlDialog
      onClose={onCloseBrowserUrlDialog}
      onSubmit={onSubmitBrowserUrl}
    />
  ) : null;
};

export default React.memo(DashboardWebsocket);
